/**
 * Registry lookups — slug/label normalization and display resolution.
 *
 * Everything that reads or writes a `technical_identifiers` entry goes through
 * here, so a system typed as `M365`, `m365`, or `Microsoft 365` lands on the one
 * slug `microsoft_365` and a label typed as `tenant id` lands on `Tenant ID`.
 * Without this the (system, label) upsert key treated those as DIFFERENT
 * entries, so the same tenant id could be stored three times on one company and
 * `remove_company_technical_identifier` would only ever delete one of them.
 */

import {
  TECHNICAL_SYSTEMS,
  type KnownTechnicalSystemSlug,
  type TechnicalIdentifierFieldDef,
  type TechnicalSystemDef,
  type TechnicalSystemSlug,
} from "./systems.js";

const BY_SLUG: ReadonlyMap<string, TechnicalSystemDef> = new Map(
  TECHNICAL_SYSTEMS.map((s) => [s.slug, s as TechnicalSystemDef]),
);

/** Alias slug → canonical slug. Built once; aliases are unique by construction. */
const ALIAS_TO_SLUG: ReadonlyMap<string, string> = new Map(
  TECHNICAL_SYSTEMS.flatMap((s) =>
    (s.slugAliases ?? []).map((alias) => [alias, s.slug] as const),
  ),
);

/**
 * Reduce free text to slug shape: lowercase, non-alphanumerics collapsed to a
 * single underscore, edges trimmed. "Microsoft 365" → "microsoft_365",
 * "AT&T" → "at_t". Pure syntax — it does not consult the registry.
 */
export function slugifySystem(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Canonical slug for whatever the caller typed. Falls back to the slugified
 * input for a system we have not catalogued, so freeform still stores something
 * consistent (and two engineers typing "Acme Portal" and "acme portal" agree).
 */
export function normalizeSystemSlug(input: string): TechnicalSystemSlug {
  const slug = slugifySystem(input);
  if (BY_SLUG.has(slug)) return slug;
  return ALIAS_TO_SLUG.get(slug) ?? slug;
}

/** Registry entry for a slug (after normalization), or `undefined` if freeform. */
export function findSystem(slug: string): TechnicalSystemDef | undefined {
  return BY_SLUG.get(normalizeSystemSlug(slug));
}

/** True when the slug (or one of its aliases) is in the registry. */
export function isKnownSystem(slug: string): slug is KnownTechnicalSystemSlug {
  return BY_SLUG.has(normalizeSystemSlug(slug));
}

/**
 * Human display name for a slug. An uncatalogued slug is title-cased from its
 * own text rather than shown raw, so the UI never renders `acme_portal`.
 */
export function systemDisplay(slug: string): string {
  const def = findSystem(slug);
  if (def) return def.display;
  const normalized = normalizeSystemSlug(slug);
  if (!normalized) return slug;
  return normalized
    .split("_")
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join(" ");
}

/** Canonical identifier fields for a system (empty when it declares none). */
export function fieldsForSystem(slug: string): readonly TechnicalIdentifierFieldDef[] {
  return findSystem(slug)?.fields ?? [];
}

/** Canonical labels for a system, for editor autocomplete + tool hints. */
export function labelsForSystem(slug: string): readonly string[] {
  return fieldsForSystem(slug).map((f) => f.label);
}

/**
 * The label an automated writer should default to for a system — its first
 * declared field. `null` when the system declares none, so callers keep their
 * own fallback rather than inventing a label that means nothing.
 */
export function primaryLabelForSystem(slug: string): string | null {
  return fieldsForSystem(slug)[0]?.label ?? null;
}

/** Comparison key for a label: case-, space- and punctuation-insensitive. */
function labelKey(label: string): string {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * Canonical casing for a label the system declares ("tenant id" → "Tenant ID").
 * An unrecognized label is returned trimmed but otherwise untouched — engineers
 * record identifiers we have not catalogued and must not have them rewritten.
 */
export function canonicalizeLabel(system: string, label: string): string {
  const trimmed = label.trim();
  const key = labelKey(trimmed);
  if (!key) return trimmed;
  for (const field of fieldsForSystem(system)) {
    if (labelKey(field.label) === key) return field.label;
  }
  return trimmed;
}

/** Field definition matching a (system, label) pair, if the system declares one. */
export function findFieldDef(
  system: string,
  label: string,
): TechnicalIdentifierFieldDef | undefined {
  const key = labelKey(label);
  return fieldsForSystem(system).find((f) => labelKey(f.label) === key);
}

/**
 * Systems worth naming in an LLM tool description — the ones that actually carry
 * canonical identifiers. The full 75-slug registry belongs in the UI picker, not
 * in every cached prompt prefix (MERIDIAN_TOKEN_REDUCTION).
 */
export const PROMPT_HINT_SYSTEMS: readonly string[] =
  TECHNICAL_SYSTEMS.filter((s) => (s.fields?.length ?? 0) > 0).map((s) => s.slug);

/** Systems relevant to the engineer (identifier) loop, for pickers. */
export const ENGINEER_SYSTEMS: readonly TechnicalSystemDef[] = TECHNICAL_SYSTEMS.filter(
  (s) => s.audiences.some((a) => a === "engineer" || a === "both"),
);
